import type Phaser from "phaser";
import { worldTextStyle } from "../text.js";

/**
 * Lightweight visual effects built from tweened primitives (no particle
 * system dependency), so they are safe across Phaser renderers.
 */
export class Effects {
  constructor(private readonly scene: Phaser.Scene) {}

  /** Confetti-style burst around a point. */
  burst(x: number, y: number, count = 24): void {
    const colors = [0xffd54f, 0x4fc3f7, 0xff8a65, 0x81c784, 0xba68c8];
    for (let i = 0; i < count; i++) {
      const rect = this.scene.add
        .rectangle(x, y, 3, 2, colors[i % colors.length]!)
        .setDepth(190_000);
      const angle = Math.random() * Math.PI * 2;
      const dist = 30 + Math.random() * 70;
      this.scene.tweens.add({
        targets: rect,
        x: x + Math.cos(angle) * dist,
        y: y + Math.sin(angle) * dist + 40,
        alpha: 0,
        duration: 900 + Math.random() * 600,
        ease: "Cubic.easeOut",
        onComplete: () => rect.destroy(),
      });
    }
  }

  /** A floating code glyph, emitted while working. Drifts behind avatar labels. */
  codePuff(x: number, y: number): void {
    const glyphs = ["{ }", "</>", "()", ";", "&&", "=>"];
    const text = this.scene.add
      .text(
        x + (Math.random() - 0.5) * 10,
        y - 20,
        glyphs[(Math.random() * glyphs.length) | 0]!,
        worldTextStyle(this.scene, { fontSize: "7px", color: "#8fd3ff" }),
      )
      .setOrigin(0.5)
      .setDepth(99_999);
    this.scene.tweens.add({
      targets: text,
      y: text.y - 14,
      alpha: 0,
      duration: 1100,
      onComplete: () => text.destroy(),
    });
  }

  /** A steam puff at the coffee machine. */
  steam(x: number, y: number): void {
    const puff = this.scene.add
      .circle(x + (Math.random() - 0.5) * 6, y - 10, 2, 0xffffff, 0.5)
      .setDepth(150_000);
    this.scene.tweens.add({
      targets: puff,
      y: puff.y - 16,
      alpha: 0,
      scale: 1.8,
      duration: 1200,
      onComplete: () => puff.destroy(),
    });
  }

  /** A music note drifting up from the hi-fi while the radio is on. */
  note(x: number, y: number): void {
    const text = this.scene.add
      .text(
        x + (Math.random() - 0.5) * 14,
        y,
        Math.random() < 0.5 ? "♪" : "♫",
        worldTextStyle(this.scene, { fontSize: "8px", color: "#ffe08a" }),
      )
      .setOrigin(0.5)
      .setDepth(150_000);
    this.scene.tweens.add({
      targets: text,
      y: text.y - 18,
      x: text.x + (Math.random() - 0.5) * 12,
      alpha: 0,
      duration: 1500,
      ease: "Sine.easeOut",
      onComplete: () => text.destroy(),
    });
  }
}
